/**
 * Ticket Hooks
 * ============
 * React Query hooks for ticket operations.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { ticketApi } from '@/services/api'
import type { TicketCreate, TicketCommentCreate, TicketSearchParams, TicketUpdate } from '@/types'

// Query keys
export const ticketKeys = {
  all: ['tickets'] as const,
  lists: () => [...ticketKeys.all, 'list'] as const,
  list: (params?: TicketSearchParams) => [...ticketKeys.lists(), params] as const,
  details: () => [...ticketKeys.all, 'detail'] as const,
  detail: (id: string | number) => [...ticketKeys.details(), id] as const,
  stats: () => [...ticketKeys.all, 'stats'] as const,
  comments: (id: string | number) => [...ticketKeys.all, 'comments', id] as const,
}

/**
 * Hook to fetch paginated ticket list.
 */
export function useTickets(params?: TicketSearchParams, options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: ticketKeys.list(params),
    queryFn: () => ticketApi.list(params),
    enabled: options?.enabled,
  })
}

/**
 * Hook to fetch a single ticket with comments.
 */
export function useTicket(id: string | number, options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: ticketKeys.detail(id),
    queryFn: () => ticketApi.get(id),
    enabled: options?.enabled !== undefined ? options.enabled && !!id : !!id,
  })
}

/**
 * Hook to fetch ticket statistics.
 */
export function useTicketStats() {
  return useQuery({
    queryKey: ticketKeys.stats(),
    queryFn: () => ticketApi.getStats(),
  })
}

/**
 * Hook to create a new ticket.
 */
export function useCreateTicket() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (data: TicketCreate) => ticketApi.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ticketKeys.lists() })
      queryClient.invalidateQueries({ queryKey: ticketKeys.stats() })
    },
  })
}

/**
 * Hook to update a ticket.
 */
export function useUpdateTicket() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ ticketId, data }: { ticketId: string | number; data: TicketUpdate }) =>
      ticketApi.update(ticketId, data),
    onSuccess: (_response, { ticketId }) => {
      queryClient.invalidateQueries({ queryKey: ticketKeys.lists() })
      queryClient.invalidateQueries({ queryKey: ticketKeys.detail(ticketId) })
      queryClient.invalidateQueries({ queryKey: ticketKeys.stats() })
    },
  })
}

/**
 * Hook to delete a ticket.
 */
export function useDeleteTicket() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (id: string | number) => ticketApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ticketKeys.lists() })
      queryClient.invalidateQueries({ queryKey: ticketKeys.stats() })
    },
  })
}

/**
 * Hook to add a comment to a ticket.
 */
export function useAddTicketComment() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ ticketId, data }: { ticketId: string | number; data: TicketCommentCreate }) =>
      ticketApi.addComment(ticketId, data),
    onSuccess: (_response, { ticketId }) => {
      queryClient.invalidateQueries({ queryKey: ticketKeys.detail(ticketId) })
      queryClient.invalidateQueries({ queryKey: ticketKeys.comments(ticketId) })
    },
  })
}

/**
 * Hook to upload a file attachment to a ticket.
 */
export function useUploadTicketAttachment() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ ticketId, file }: { ticketId: string | number; file: File }) =>
      ticketApi.uploadAttachment(ticketId, file),
    onSuccess: (_response, { ticketId }) => {
      queryClient.invalidateQueries({ queryKey: ticketKeys.detail(ticketId) })
      queryClient.invalidateQueries({ queryKey: ticketKeys.lists() })
    },
  })
}

/**
 * Hook to delete a file attachment from a ticket.
 */
export function useDeleteTicketAttachment() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ ticketId, attachmentId }: { ticketId: string | number; attachmentId: number }) =>
      ticketApi.deleteAttachment(ticketId, attachmentId),
    onSuccess: (_response, { ticketId }) => {
      queryClient.invalidateQueries({ queryKey: ticketKeys.detail(ticketId) })
      queryClient.invalidateQueries({ queryKey: ticketKeys.lists() })
    },
  })
}
